import React, { useId, useState } from 'react';
import { quickActions } from './quickActions';
import { parseDateInput, toDateInputValue } from './taskModel';

// Portal Work — the mobile quick-action bar.
//
// Anthony, 2026-08-13, showing a phone task manager: "if you look at the
// highlighted task, you'll see that it gives buttons to add a date, a location,
// a hashtag flag, or attach a photo. I was thinking something similar, so
// thinking in a mobile-first perspective."
//
// So: tap a row on a phone and it expands in place to reveal one 44px button per
// action — date, assignee, priority, area, park — plus a way into the full task.
// Tapping one opens its options directly underneath. Nothing floats and nothing
// navigates.
//
// TWO STRUCTURAL DECISIONS, BOTH LEARNED THE HARD WAY EARLIER THE SAME DAY:
//
// 1. THE MENU RENDERS IN NORMAL FLOW, NOT AS AN ANCHORED POPOVER. The desktop
//    due-cell control (DueQuickSet) hangs an absolutely-positioned panel out of
//    the row, and that panel can be painted under LATER rows — it needs a
//    z-index bump to be clickable at all. An in-flow menu has no stacking
//    context to lose, cannot be clipped by an ancestor, and cannot open
//    off-screen. On a phone it is also simply the better shape: full row width
//    instead of a 216px panel squeezed against the edge.
// 2. THE BAR STAYS OPEN AFTER A WRITE; ONLY THE MENU CLOSES. Triage is the real
//    use case — a raw capture usually needs an area AND a priority AND a date —
//    and collapsing the whole row after each one would make the common case
//    three taps of re-opening.
//
// Every handler stops propagation: the row is itself role="button".
function TaskQuickBar({
	task,
	now,
	fields,
	areaOrder,
	offeredGates,
	onQuickUpdate,
	onOpen,
}) {
	const [activeKey, setActiveKey] = useState(null);
	const baseId = useId();
	const actions = quickActions(task, { now, fields, areaOrder, offeredGates });
	const active = actions.find((a) => a.key === activeKey) || null;

	const stop = (e) => e.stopPropagation();

	const apply = (update) => {
		onQuickUpdate?.(task, update);
		setActiveKey(null);
	};

	return (
		<div
			className="wt-qbar-wrap"
			onClick={stop}
			onPointerDown={stop}
			onKeyDown={(e) => {
				e.stopPropagation();
				if (e.key === 'Escape' && activeKey) {
					e.preventDefault();
					setActiveKey(null);
				}
			}}>
			<div className="wt-qbar" role="group" aria-label="Quick actions">
				{actions.map((a) => (
					<button
						key={a.key}
						type="button"
						className={`wt-qbar__btn${a.summary ? ' is-set' : ''}${
							activeKey === a.key ? ' is-active' : ''
						}`}
						aria-expanded={activeKey === a.key}
						aria-controls={activeKey === a.key ? `${baseId}-menu` : undefined}
						aria-label={a.summary ? `${a.label}: ${a.summary}` : `Set ${a.label.toLowerCase()}`}
						onClick={(e) => {
							e.stopPropagation();
							setActiveKey((k) => (k === a.key ? null : a.key));
						}}>
						<i className={`fa-sharp fa-solid ${a.icon}`} aria-hidden="true" />
						<span className="wt-qbar__cap">{a.summary || a.label}</span>
					</button>
				))}
				<button
					type="button"
					className="wt-qbar__btn wt-qbar__btn--open"
					aria-label="Open the full task"
					onClick={(e) => {
						e.stopPropagation();
						onOpen?.(task);
					}}>
					<i
						className="fa-sharp fa-solid fa-arrow-up-right-from-square"
						aria-hidden="true"
					/>
					<span className="wt-qbar__cap">Open</span>
				</button>
			</div>

			{active && (
				<div
					className="wt-qbar__menu"
					id={`${baseId}-menu`}
					role="menu"
					aria-label={active.label}>
					{active.options.map((o) => (
						<button
							key={o.key}
							type="button"
							role="menuitem"
							className={`wt-qbar__opt${o.current ? ' is-current' : ''}${
								o.destructive ? ' is-destructive' : ''
							}`}
							onClick={(e) => {
								e.stopPropagation();
								apply(o.update);
							}}>
							{o.initials ? (
								<span
									className="wt-qbar__initials"
									style={{ background: `var(${o.colorVar})` }}
									aria-hidden="true">
									{o.initials}
								</span>
							) : (
								<i
									className={`fa-sharp fa-solid ${
										o.icon || (o.areaKey ? 'fa-layer-group' : 'fa-calendar')
									} wt-qbar__opticon`}
									aria-hidden="true"
								/>
							)}
							<span className="wt-qbar__optlabel">{o.label}</span>
							{o.hint && <span className="wt-qbar__opthint">{o.hint}</span>}
							{o.current && (
								<i
									className="fa-sharp fa-solid fa-check wt-qbar__check"
									aria-hidden="true"
								/>
							)}
						</button>
					))}

					{active.key === 'due' && (
						<div className="wt-qbar__custom">
							<label
								className="wt-qbar__customlabel"
								htmlFor={`${baseId}-date`}>
								Pick a date
							</label>
							<input
								id={`${baseId}-date`}
								type="date"
								className="wt-qbar__input"
								value={toDateInputValue(task?.deadline)}
								onClick={stop}
								onChange={(e) => {
									// parseDateInput, never `new Date(value)` — see taskModel.
									const d = parseDateInput(e.target.value);
									if (d) apply({ deadline: d });
								}}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

export default TaskQuickBar;
